import React, { createContext, useContext, useRef, useState } from "react";
import { DataMessages, useData, useErrors } from "../core/hooks/useData";
import { AllElementTypes, EventContext, FieldMessages, PresenterMessages, useContainer, usePresenter } from "./types";
import { broadcast, subscribe, unsubscribe } from "../core/hooks/useChannel";
import { newID } from "../core/utils/unique";
import { EditableRegion } from "../core/form/EditableRegion";
import { InspectorMessages, inspectorChannel } from "./Inspector";
import { Icon } from "../core/ui/icons";
import { useOptions } from "./Container";
import { EntitySearch } from "./EntityLookup";


const FieldContext = createContext({} as any);
export function useFieldChannelID() {
	return useContext(FieldContext);
}

let renderCount = 0;
const uniqueFields = [] as any[];

export function Field(props: {
	def: AllElementTypes,
	component: any,
	
	noLabel?: boolean,
	noErrors?: boolean,
}) {

	// renderCount++;
	// console.log(renderCount);

	const { def } = props;

	// if (!TESTING_RENDERS) {
	const data = useData<any>();
	const errors = useErrors(data, def.id, props.noErrors);
	// }

	const presenter = usePresenter();
	const container = useContainer();

	//const [isVisible, setIsVisible] = React.useState(true);
	const visibleRef = useRef(true);

	const [,refresh] = useState({});

	const channelID = React.useRef<string>();
	if (!channelID.current) channelID.current = newID();

	const surroundingOptions = useOptions();
	const surroundingOptionsRef = useRef(surroundingOptions);
	if (surroundingOptionsRef.current !== surroundingOptions) surroundingOptionsRef.current = surroundingOptions;

	// NOTE: if there is a script default value then couldn't run it at the point in createElement
	// where the "static" default value is set if necessary
	// this is because the script needs the proper context to run and its not available until now
	React.useEffect(() => {
		// do we have script to run to set the default value
		if (def.defaultValueScript && def.id && container.data.value[def.id] === undefined) {
			const eventContext: EventContext = {
				container,
				dataWindows: [{
					name: "data",
					obj: container.data,
				}],
				def,
				presenter,
				type: "default-value",
				options: surroundingOptions,
				debugContext: [
					"default-value"
				]				
			}
			const val = presenter.scriptEngine.run(def.defaultValueScript, eventContext);
			container.data.setValue(def.id, val);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [def.id, def.defaultValueScript]);
	
	React.useLayoutEffect(() => {
		const sub = subscribe(channelID.current!, (msg) => {
			if (msg.type === FieldMessages.SET_VISIBILITY) {
				if (msg.visible !== visibleRef.current) {
					visibleRef.current = msg.visible;

					// if hiding then have to clear out all data as well
					if (!msg.visible) {
						delete (container.data.value as any)[def.id];
					} else if (container.data.value[def.id] === undefined) {
						// field just became visible with no current value — apply default
						if (def.defaultValue !== undefined) {
							container.data.setValue(def.id, def.defaultValue);
						} else if (def.defaultValueScript) {
							const eventContext: EventContext = {
								container,
								dataWindows: [{ name: "data", obj: container.data }],
								def,
								presenter,
								type: "default-value",
								options: surroundingOptionsRef.current,
								debugContext: ["default-value", "visibility-change"],
							};
							const val = presenter.scriptEngine.run(def.defaultValueScript, eventContext);
							container.data.setValue(def.id, val);
						}
					}

					refresh({});

					// change in visibility requires calc fields to re-run because could have 
					// removed data that results in different output
					broadcast(presenter.channelID, {
						type: PresenterMessages.RECALC_NEEDED,
					});

				}

			} else if (msg.type === FieldMessages.GET_VISIBILITY) {
				// if field itself is not visible then return that
				//but even if field is visible have to track back through parent containers in case any of them aren't 
				if (!visibleRef.current) {
					msg.visible = false;
					return;
				}

				// parents
				let c = container.parentContainer;
				let fieldID = container.def.id;
				const m = {
					type: FieldMessages.GET_VISIBILITY,
					visible: true,
				};
				while (c && c.def) {
					const f = c.fields[fieldID];
					if (!f) break;

					m.visible = true;
					broadcast(f.channelID, msg);
					if (!m.visible) {
						// one of the parents is not visible
						msg.visible = false;
						return;
					}

					// try next parent
					fieldID = c.def.id;
					c = c.parentContainer!;
				}

				msg.visible = true;

			} else if (msg.type === FieldMessages.GET_DEF) {
				msg.def = def;

			} else if (msg.type === FieldMessages.GET_CONTAINER_DEF) {
				msg.def = container.def;

			} else if (msg.type === FieldMessages.GET_CONTAINER) {
				msg.container = container;
				msg.def = def; // include def as well in case handy

			} else if (msg.type === FieldMessages.REFRESH_PARENT_CONTAINER) {
				container.refresh();

			}
		});

		// if there are any events on this then subscribe to global presenter messages also
		let subGlobalEvents: any;
		if (def.events) {
			subGlobalEvents = subscribe(presenter.channelID, (msg) => {
				if (msg.type === DataMessages.CHANGE) {
					if (def.events?.change) {
						presenter.triggerEvent({
							dataWindows: [
								{
									name: "data",
									obj: container.data,
								},
								{
									name: "msg",
									obj: msg,
								},
							],
							def,
							type: "change",
							container,
							presenter,
							options: surroundingOptionsRef.current,
							debugContext: [
								"change"
							]
						});
					}

				} else if (msg.type === PresenterMessages.BEFORE_SAVE) {
					if (def.events?.["before-save"]) {
						presenter.triggerEvent({
							dataWindows: [{
								name: "data",
								obj: container.data,
							}],
							def,
							type: "before-save",
							container,
							presenter,
							options: surroundingOptionsRef.current,
							debugContext: [
								"before-save"
							]
						});
					}

				}
			});
		}

		// every field needs to listen to these global events:
		const subGlobalAll = subscribe(presenter.channelID + "/all-fields", (msg) => {
			if (msg.type === PresenterMessages.SET_VALUE) {
				if (container.propPath + def.id === msg.prop) {
					container.data.setValue(def.id, msg.value);
					msg.cancelled = true;
				}

			} else if (msg.type === PresenterMessages.GET_FIRST_FIELD) {
				if (
					(!msg.matchFullPath && def.id === msg.prop)
					|| (msg.matchFullPath && (container.propPath + def.id === msg.prop))
				 ) {
					msg.field = def;
					msg.channel = channelID.current;
					msg.cancelled = true;
				}

			}
		});

		return () => {
			unsubscribe(presenter.channelID + "/all-fields", subGlobalAll);

			unsubscribe(channelID.current!, sub);

			if (subGlobalEvents) unsubscribe(presenter.channelID, subGlobalEvents);
		}
	}, [presenter, container, def]);

	React.useLayoutEffect(() => {
		// const uid = container.data.id + "/" + def.id;
		// if (uniqueFields.indexOf(uid) < 0) {
		// 	uniqueFields.push(uid);
		// 	console.log("unique fields: " + uniqueFields.length);
		// }

		container.registerField({ field: def, channelID: channelID.current! });

		return () => {
			container.unregisterField(def);
		}
	}, [container, def]);

	const errorItems = [];
	let i = 0;
	for(let e of errors) {
		errorItems.push(
			// <span key={i++} className="error bg-error text-inverse text-center">{e.message}</span>
			<span key={i++} className="tag incomplete margin-left-5 text-center">{e.message}</span>
		);
	}


	// NOTE: on initial load
	// have to trigger a change so that the visibility logic runs properly
	React.useLayoutEffect(() => {
		if (!visibleRef.current) return;
		
		if (def.events?.change) {
			presenter.triggerEvent({
				dataWindows: [{
					name: "data",
					obj: container.data,
				}],
				def,
				type: "change",
				container,
				presenter,
				options: surroundingOptions ? surroundingOptions : {},
				debugContext: [
					`${def.id}.change`
				]
			});
		}		
	}, [visibleRef.current, def, presenter, container.data, container]);


	if (
		!visibleRef.current
		&& !presenter.designMode
	) return null;


	let title: any = def.title;
	let subTitle: any = def.subTitle ? <div className="subtitle font-small italic">{def.subTitle}</div> : undefined;
	if (presenter.designMode) {
		// have titles to be editable regions
		title = 
			<EditableRegion
				intialHtml={title}
				onChange={(e) => {
					def.title = e.target.value;
				}}
			/>;
		
		if (subTitle) {
			subTitle = 
				<EditableRegion
					intialHtml={def.subTitle}
					onChange={(e) => {
						def.subTitle = e.target.value;
					}}
					className="font-small italic"
				/>;
		}
	}

	if (def.type === "calc" && !presenter.designMode) {
		return (
			<div>
				{props.component}
				{createFeatures()}
			</div>
		);
	}

	const features = createFeatures();


	const labels = 
		(props.noLabel || !def.title || def.type === "button") ? undefined :
		<div className="labels flex vert">
			<label>
				{title}
				{subTitle}
				{features}
			</label>
			
		</div>
	;

	let handleDesignerKeyDown;
	if (presenter.designMode) {
		handleDesignerKeyDown = function(e: any) {
			let handled = false;

			if (e.metaKey) {
				if (e.code === "KeyE") {
					// shortcut for opening edit modal
					broadcast(
						inspectorChannel,
						{
							type: InspectorMessages.EDIT,
							def: def,
							fieldID: channelID.current,
						}
					);
					handled = true;

				} else if (e.code === "ArrowDown") {
					if (e.shiftKey) {
						broadcast(inspectorChannel, { type: InspectorMessages.MOVE_DOWN });
						handled = true;	

					} else {
						broadcast(inspectorChannel, { type: InspectorMessages.ADD_BELOW });
						handled = true;	
					}

				} else if (e.code === "ArrowUp") {
					if (e.shiftKey) {
						broadcast(inspectorChannel, { type: InspectorMessages.MOVE_UP });
						handled = true;	
					} else {
						broadcast(inspectorChannel, { type: InspectorMessages.ADD_ABOVE });
						handled = true;
					}

				} else if (e.code === "Backspace") {
					broadcast(inspectorChannel, { type: InspectorMessages.DELETE });
					handled = true;					
					
				}
			}

			if (e.ctrlKey && e.altKey && !e.shiftKey && !e.metaKey) {
				if (e.code === "Digit1") {
					// change to heading 
					def.type = "heading";
					container.refresh();
					handled = true;					
				}
			}

			if (!e.ctrlKey && !e.altKey && e.shiftKey && e.metaKey) {
				if (e.code === "KeyC") {
					def.type = "checkbox";
					container.refresh();
					handled = true;					

				} else if (e.code === "KeyD") {
					def.type = "dropdown";
					container.refresh();
					handled = true;					

				} else if (e.code === "KeyR") {
					def.type = "radio";
					container.refresh();
					handled = true;				

				}

			}

			if (handled) {
				e.stopPropagation();
				e.preventDefault();
			}

		};
	}

	const showEvenThoughHidden = (!visibleRef.current && presenter.designMode);

	// does the field have any features to display as well?
	function createFeatures() {
		const features = [];
		if (def.features) {
			for(let f of def.features) {
				if (f.type === "entity-search") {
					let options;
					if (f.options) {
						options = presenter.scriptEngine.run(f.options, undefined);
					}
					features.push(
						<EntitySearch key={f.type} options={options} />
					);
				}
			}
		}
		
		return (features.length > 0) ? features : undefined;
	}


	const autoSizeHeight = (def.type === "report");
	return (
		<FieldContext.Provider value={channelID}>
			<div
				className={
					"field field-"+def.type+" flex horiz width-100pc " //+ (errors.length > 0 ? "has-errors" : "")
					+ (showEvenThoughHidden ? " opacity-qtr" : "")
					+ (labels ? " has-label" : " has-no-label")
					+ (autoSizeHeight ? " auto-size-height" : "")
				}
				data-key={channelID.current}
				id={channelID.current}
				onKeyDown={handleDesignerKeyDown}
			>
				{labels}

				{
					showEvenThoughHidden 
						? <div className="flex horiz gap-2"><Icon type="eye" />Not Visible</div> 
						: props.component
				}

				{errorItems.length > 0 &&
					<div className="flex wrap align-center font-small">
						{errorItems}
					</div>
				}

			</div>
		</FieldContext.Provider>
	)
}